/**
 * SPDX-FileCopyrightText: 2022 Nextcloud GmbH and Nextcloud contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

import { registerWidget, registerCustomPickerElement, NcCustomPickerRenderResult } from '@nextcloud/vue/dist/Functions/registerReference.js'
import { translate, translatePlural } from '@nextcloud/l10n'
import storeFactory from './store/main.js'

import './shared-init.js'

const prepareVue = async (Component = null, store = null) => {
	const { default: Vue } = await import('vue')
	const { default: ClickOutside } = await import('vue-click-outside')
	const { createPinia, PiniaVuePlugin } = await import('pinia')

	Vue.prototype.t = translate
	Vue.prototype.n = translatePlural
	Vue.prototype.OC = window.OC
	Vue.prototype.OCA = window.OCA
	Vue.directive('click-outside', ClickOutside)
	Vue.directive('focus', {
		inserted(el) {
			el.focus()
		},
	})
	Vue.use(PiniaVuePlugin)

	const pinia = createPinia()
	if (store) {
		pinia.use(() => ({ $vuex: store }))
	}

	if (!Component) {
		return { Vue, pinia }
	}

	return {
		Widget: Vue.extend(Component),
		pinia,
	}
}

registerWidget('deck-card', async (el, { richObjectType, richObject, accessible }) => {
	const { default: CardReferenceWidget } = await import('./views/CardReferenceWidget.vue')
	const { Widget, pinia } = await prepareVue(CardReferenceWidget)
	// trick to change the wrapper element size, otherwise it always is 100%
	// which is not very nice with a simple card
	el.parentNode.style['max-width'] = '400px'
	el.parentNode.style['margin-left'] = '0'
	el.parentNode.style['margin-right'] = '0'
	new Widget({
		pinia,
		propsData: {
			richObjectType,
			richObject,
			accessible,
		},
	}).$mount(el)
})

const boardWidgets = {}
registerWidget('deck-board', async (el, { richObjectType, richObject, accessible, interactive }) => {
	const { default: BoardReferenceWidget } = await import('./views/BoardReferenceWidget.vue')
	const store = storeFactory()
	const { Widget, pinia } = await prepareVue(BoardReferenceWidget, store)
	boardWidgets[el] = new Widget({
		store,
		pinia,
		propsData: {
			richObjectType,
			richObject,
			accessible,
			interactive,
		},
	}).$mount(el)
}, (el) => {
	boardWidgets[el].$destroy()
	delete boardWidgets[el]
})

registerWidget('deck-comment', async (el, { richObjectType, richObject, accessible }) => {
	const { default: CommentReferenceWidget } = await import('./views/CommentReferenceWidget.vue')
	const { Widget, pinia } = await prepareVue(CommentReferenceWidget)

	el.parentNode.style['max-width'] = '400px'
	el.parentNode.style['margin-left'] = '0'
	el.parentNode.style['margin-right'] = '0'

	new Widget({
		pinia,
		propsData: {
			richObjectType,
			richObject,
			accessible,
		},
	}).$mount(el)
})

registerCustomPickerElement('create-new-deck-card', async (el, { providerId, accessible }) => {
	const { default: CreateNewCardCustomPicker } = await import('./views/CreateNewCardCustomPicker.vue')
	const { Widget: Element, pinia } = await prepareVue(CreateNewCardCustomPicker)
	const vueElement = new Element({
		pinia,
		propsData: {
			providerId,
			accessible,
		},
	}).$mount(el)
	return new NcCustomPickerRenderResult(vueElement.$el, vueElement)
}, (el, renderResult) => {
	renderResult.object.$destroy()
}, 'normal')
